<template>
    <AddFileSection
        :id="AddFileFormSectionIds.SEND_WIRE_INSTRUCTIONS"
        sectionDescription="Select clients to send secure wire instructions for this file."
        sectionHeading="Send wire instructions"
    >
        <div class="mt-3 grid gap-4">
            <SelectWireInstructions
                v-model:wireDoc="addFileStore.wireDoc"
                :branchId="addFileStore.branch?.id"
                :disabled="isSelectWireDocDisabled"
                :error="wireDocError"
                @optionSelected="clearServerValidationError(FileDetailErrorKeys.WIRE_DOC_ID)"
            />
            <ClientMultiSelect
                v-if="showRecipients"
                :clientOptions
                :error="wireDocRecipientsError"
                :name="WIRE_DOC_RECIPIENTS_NAME"
                @update:modelValue="onRecipientsUpdate"
            />
        </div>
    </AddFileSection>
</template>

<script setup lang="ts">
import { ClientSelectionErrorKeys, FileDetailErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { availableClients } from '@/company-portal/add-file/available-clients';
import { WIRE_DOC_RECIPIENTS_NAME } from '@/company-portal/add-file/selectors';
import { useAddFileError } from '@/company-portal/add-file/use-add-file-error';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import ClientMultiSelect from '@/components/Views/Company/AddFile/ClientMultiSelect.vue';
import type { ClientOption, ClientType } from '@/components/Views/Company/File/file.types';
import SelectWireInstructions from '@/components/Views/Company/shared/SelectWireInstructions.vue';
import { filterWireDocsByBranch } from '@/components/Views/Company/shared/wire-instructions-utils';
import { joinFirstLast } from '@/shared/utils/strings';
import { useUserStore } from '@/stores/company/user.store';
import { computed, watchEffect } from 'vue';

withDefaults(
    defineProps<{
        showRecipients?: boolean;
    }>(),
    {
        showRecipients: true,
    },
);

const addFileStore = useAddFileStore();
const userStore = useUserStore();
const { serverValidationErrors, clearServerValidationError } = useAddFileError();

// wire instructions
const tpsWireDoc = computed(() => {
    const wireDocId = addFileStore.tpsOrder?.wire_doc_id;
    if (!wireDocId) {
        return;
    }
    return filterWireDocsByBranch(userStore.availableWireDocs, addFileStore.branch?.id).find(wireDoc => wireDoc.id === wireDocId);
});

watchEffect(() => {
    addFileStore.wireDoc = tpsWireDoc.value;
});

const isSelectWireDocDisabled = computed(
    () => !addFileStore.branch || (!!tpsWireDoc.value && !addFileStore.tpsOrder?.force_wire_doc_selection_true),
);

// clients
const clientOptions = computed<ClientOption[]>(() =>
    availableClients.new.map(client => {
        const isRequestWireInfoClient = addFileStore.requestWireInfoClientIds.includes(client.id);

        return {
            id: client.id,
            value: client.id,
            email: client.email,
            first: client.first,
            last: client.last,
            label: joinFirstLast(client.first, client.last),
            phone: client.phone,
            /** @todo cleanup in [CLENG-883] assertion is needed because we have duplicate type declarations for client role  */
            role: client.role as ClientType,
            disabled: isRequestWireInfoClient,
            ...(isRequestWireInfoClient && {
                helperText: 'This client is already selected to request wire information.',
            }),
        };
    }),
);

const onRecipientsUpdate = (clients: ClientOption[]) => {
    addFileStore.wireDocRecipientClientIds = clients.map(client => client.id);
    clearServerValidationError(ClientSelectionErrorKeys.WIRE_DOC_RECIPIENTS);
};

// errors
const wireDocError = computed(() => serverValidationErrors.value?.wireDoc_id?.join(' '));
const wireDocRecipientsError = computed(() =>
    serverValidationErrors.value?.[ClientSelectionErrorKeys.WIRE_DOC_RECIPIENTS]?.join(' '),
);
</script>
